import React from "react";
import { RecIcon } from "../materials/MaterialRecordParts";
import { dueState, formatTaskDate, priorityLabel, TASK_STATUS } from "../../helpers/tasks/taskModel";
import {
  DURATION_UNITS,
  formatDuration,
  PROGRESS_STATE,
  progressValue,
  taskProgressState,
} from "../../helpers/tasks/taskDuration";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import "../materials/materialRecord.css";
import "./tasks.css";

export function todayIso(now = new Date()) {
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

export function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** Completion box. A button when the user may change it, a plain mark otherwise. */
export function TaskCheck({ task, interactive, busy, onToggle }) {
  const done = task.status === TASK_STATUS.COMPLETED;
  const box = (
    <span className={`tk-check${done ? " is-done" : ""}`} aria-hidden="true">
      {done ? <RecIcon name="check" /> : null}
    </span>
  );
  if (!interactive) return <span className="tk-check-wrap">{box}</span>;
  return (
    <button
      type="button"
      className="tk-check-wrap tk-check-btn"
      role="checkbox"
      aria-checked={done}
      aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation();
        onToggle(task);
      }}
    >
      {box}
    </button>
  );
}

export function PriorityChip({ priority }) {
  if (!priority) return null;
  return (
    <span className={`tk-chip tk-chip--${priority}`}>
      <RecIcon name="flag" />
      {priorityLabel(priority)}
    </span>
  );
}

export function DueChip({ task, today = todayIso() }) {
  if (!task.due_date) return null;
  const state = dueState(task, today);
  return (
    <span className={`tk-chip tk-chip--due${state ? ` is-${state}` : ""}`}>
      <RecIcon name="calendar" />
      {state === "overdue" ? "Overdue · " : ""}Due {formatTaskDate(task.due_date)}
    </span>
  );
}

export function StatusChip({ status }) {
  const done = status === TASK_STATUS.COMPLETED;
  return (
    <span className={`tk-chip tk-chip--status${done ? " is-done" : ""}`}>
      {done ? <RecIcon name="check" /> : null}
      {done ? "Completed" : "Open"}
    </span>
  );
}

/**
 * Planned duration input: amount + Hours/Days. The value is stored as integer
 * minutes (1 day = the org Work Schedule hours per day); it is never derived
 * from the dates. Without a known schedule (minutesPerDay null) only Hours is
 * offered.
 */
export function DurationInput({ id, amount, unit, onChange, minutesPerDay = null, disabled = false }) {
  const units = minutesPerDay ? DURATION_UNITS : DURATION_UNITS.filter((o) => o.value === "hours" || o.value === unit);
  return (
    <span className="tk-duration">
      <input
        id={id}
        className="rec-input tk-duration__amount"
        inputMode="decimal"
        placeholder="e.g. 4"
        value={amount}
        disabled={disabled}
        onChange={(e) => onChange({ amount: e.target.value, unit })}
        aria-describedby={`${id}-unit`}
      />
      <span className="rec-select tk-duration__unit">
        <select
          id={`${id}-unit`}
          className="rec-input"
          aria-label="Duration unit"
          value={unit}
          disabled={disabled}
          onChange={(e) => onChange({ amount, unit: e.target.value })}
        >
          {units.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </span>
    </span>
  );
}

/** Reported progress bar: green = done share, gold = remaining (design tokens). */
export function ProgressBar({ value, label }) {
  if (value == null) return null;
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <span className="tk-progress" role="img" aria-label={label || `${pct}% complete`}>
      <span className="tk-progress__done" style={{ width: `${pct}%` }} />
    </span>
  );
}

export function DurationChip({ minutes }) {
  const { minutesPerDay } = useWorkSchedule();
  const label = formatDuration(minutes, minutesPerDay);
  if (!label) return null;
  return (
    <span className="tk-chip tk-chip--duration" title="Planned duration">
      <RecIcon name="clock" />
      {label}
    </span>
  );
}

/**
 * Canonical task progress/status pill (Not Started / In Progress · N% /
 * Completed). Same chip tokens as the Tasks list; nothing is inferred.
 */
export function TaskStatePill({ task, hideNotStarted = false }) {
  const { state, label } = taskProgressState(task);
  if (hideNotStarted && state === PROGRESS_STATE.NOT_STARTED) return null;
  const tone =
    state === PROGRESS_STATE.COMPLETED
      ? "tk-chip--status is-done"
      : state === PROGRESS_STATE.IN_PROGRESS
        ? "tk-chip--progress"
        : "tk-chip--notstarted";
  return (
    <span className={`tk-chip ${tone}`} data-testid="task-state-pill" data-state={state}>
      {state === PROGRESS_STATE.COMPLETED ? <RecIcon name="check" /> : null}
      {label}
    </span>
  );
}

export function ProgressChip({ task }) {
  const value = progressValue(task);
  if (value == null || task.status === TASK_STATUS.COMPLETED) return null;
  return (
    <span className="tk-chip tk-chip--progress" title="Reported progress">
      {value}%
    </span>
  );
}
